import React, { useState, useMemo } from 'react';
import { FolderOpen, Upload, ChevronRight, Search, X } from 'lucide-react';
import { safeAsync, Badge, emptyMsg, btnPrimary, cardWrap, neuShadowSm, DOC_TYPES } from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

export default function DocsTab({ documents, canCreate, loadExtended }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const DOC_TYPE_COLORS = {
    general: '#6C757D',
    proposal: '#2E8B98',
    contract: '#256F7A',
    consent: '#62BB46',
    insurance: '#F26522',
    report: '#2E8B98',
    invoice: '#4FA038',
    other: '#ADB5BD'
  };

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(d =>
      matchesNameQuery(
        [d.title, d.file_name, d.first_name, d.last_name], query));
  }, [documents, searchQuery]);

  // Group filtered documents by doc_type
  const groupedDocuments = useMemo(() => {
    const groups = {};
    filteredDocuments.forEach(d => {
      const type = d.doc_type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(d);
    });
    return groups;
  }, [filteredDocuments]);

  // Auto-expand all groups when search is active
  const isSearchActive = searchQuery.trim().length > 0;
  const effectiveExpandedGroups = isSearchActive ? new Set(Object.keys(groupedDocuments)) : expandedGroups;

  const toggleGroup = (groupType) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupType)) {
      newExpanded.delete(groupType);
    } else {
      newExpanded.add(groupType);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(Object.keys(groupedDocuments)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleAddDocument = safeAsync(async () => {
    const title = prompt('Document title:');
    if (!title) return;
    const fileName = prompt('File name:', `${title.toLowerCase().replace(/\s+/g, '_')}.pdf`);
    const docType = prompt('Type (general/proposal/contract/consent/insurance/report/invoice):', 'general');
    const contactId = prompt('Link to contact ID (optional):');
    await window.zignagenixAPI.createCRMDocument({
      title,
      file_name: fileName,
      file_path: `/documents/${fileName}`,
      doc_type: docType || 'general',
      contact_id: contactId ? parseInt(contactId) : null
    });
    loadExtended();
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FolderOpen size={18} color="#2E8B98" /> Document Management
        </h3>
        {canCreate && (
          <button onClick={handleAddDocument} style={btnPrimary}>
            <Upload size={13} /> Add Document
          </button>
        )}
      </div>

      {/* Doc type summary */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {DOC_TYPES.map(dt => {
          const count = documents.filter(d => d.doc_type === dt).length;
          return (
            <div key={dt} style={{ background: '#F8F9FA', borderRadius: 10, boxShadow: neuShadowSm, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: DOC_TYPE_COLORS[dt] || '#6C757D' }}>{count}</div>
              <div style={{ fontSize: 9, color: '#6C757D', textTransform: 'capitalize' }}>{dt}</div>
            </div>
          );
        })}
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, color: '#6C757D', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search by title, file name, or contact..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              border: '1px solid #DEE2E6',
              borderRadius: 8,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => (e.target.style.borderColor = '#2E8B98')}
            onBlur={(e) => (e.target.style.borderColor = '#DEE2E6')}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              style={{
                position: 'absolute',
                right: 12,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#6C757D'
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Expand/Collapse All Buttons */}
      {Object.keys(groupedDocuments).length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            onClick={expandAll}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              color: '#2E8B98',
              background: '#E8F4F8',
              border: '1px solid #2E8B98',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#2E8B98';
              e.target.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#E8F4F8';
              e.target.style.color = '#2E8B98';
            }}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              color: '#6C757D',
              background: '#F8F9FA',
              border: '1px solid #DEE2E6',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#6C757D';
              e.target.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#F8F9FA';
              e.target.style.color = '#6C757D';
            }}
          >
            Collapse All
          </button>
        </div>
      )}

      {/* Accordion Groups */}
      <div style={cardWrap}>
        {filteredDocuments.length === 0 ? (
          <div style={emptyMsg}>
            {searchQuery ? 'No documents match your search.' : 'No documents uploaded yet.'}
          </div>
        ) : Object.keys(groupedDocuments).length === 0 ? (
          <div style={emptyMsg}>No documents in this category.</div>
        ) : (
          Object.entries(groupedDocuments).map(([docType, docs]) => {
            const isExpanded = effectiveExpandedGroups.has(docType);
            const docCount = docs.length;
            const groupColor = DOC_TYPE_COLORS[docType] || '#6C757D';

            return (
              <div key={docType} style={{ borderBottom: '1px solid #E9ECEF' }}>
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(docType)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                    transition: 'background-color 0.2s',
                    borderRadius: 0
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F8F9FA')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {/* Chevron Icon */}
                  <ChevronRight
                    size={18}
                    color={groupColor}
                    style={{
                      transition: 'transform 0.3s ease',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      flexShrink: 0
                    }}
                  />

                  {/* Group Title */}
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#212529', textTransform: 'capitalize', flex: 1 }}>
                    {docType}
                  </span>

                  {/* Count Badge */}
                  <Badge text={docCount.toString()} color={groupColor} />
                </button>

                {/* Group Content with Smooth Transition */}
                <div
                  style={{
                    maxHeight: isExpanded ? `${docs.length * 60}px` : '0px',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease-in-out',
                    backgroundColor: '#F8F9FA'
                  }}
                >
                  {docs.map((d) => (
                    <div
                      key={d.id}
                      style={{
                        padding: '10px 16px 10px 44px',
                        borderBottom: '1px solid #E9ECEF',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F0F3F5')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F8F9FA')}
                    >
                      <FolderOpen size={14} color={groupColor} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{d.title}</span>
                          {d.is_template && <Badge text="TEMPLATE" color="#d946ef" />}
                        </div>
                        <div style={{ fontSize: 10, color: '#6C757D' }}>
                          {d.file_name} {d.first_name ? `— ${d.first_name} ${d.last_name}` : ''} · v{d.version} · {d.uploader_name}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: '#ADB5BD', flexShrink: 0 }}>
                        {new Date(d.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
